import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useFieldArray, useFormState } from 'react-hook-form';
import { Alert, Box, Button, Collapse, Divider, Typography, useTheme } from '@mui/material';
import { AddCircle } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import fieldRenderers from '@ui/pages/EventRegistration/RegistrationForm/fieldRenderers';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Shared "Person 1 / + See more / max reached" repeating-group UI for the Additional Attendees
// question type. Used from the attendee-facing registration form and both admin response dialogs -
// one implementation, three call sites - reusing the same generic fieldRenderers table every other
// question type already uses, so no new input widgets are needed for any sub-question type.
const AdditionalAttendeesGroup = ({ fieldId, label, maxAdditionalAttendees = 2, maxAdditionalAttendeesReachedMessage, initialPeople, limitReachedMessage, subFieldDefinitions, control, disabled = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: `${fieldId}.people`
    });
    // Sub-field renderers don't read react-hook-form's fieldState themselves - every other field in
    // this form is passed its error message explicitly by its parent, so this component must do the
    // same for each nested `${fieldId}.people.${personIndex}.${subField.id}` path.
    const { errors } = useFormState({ control });
    const [collapsedPersonIds, setCollapsedPersonIds] = useState(() => new Set());
    const togglePersonDetails = (personId) => {
        setCollapsedPersonIds((prev) => {
            const next = new Set(prev);
            if (next.has(personId)) {
                next.delete(personId);
            }
            else {
                next.add(personId);
            }
            return next;
        });
    };
    // Person 1's card should already be open when the question first appears - "See more" is only
    // for adding person 2 onward, not for revealing the first person. When an edit dialog hydrates
    // existing people asynchronously, `initialPeople` arrives after mount - re-run on that change
    // (via replace(), which is the only field-array method that keeps `fields` and the underlying
    // values in sync) rather than trying to detect it purely from `fields.length` on mount.
    useEffect(() => {
        if (initialPeople !== undefined) {
            replace((initialPeople.length > 0 ? initialPeople : [{}]));
        }
        else if (fields.length === 0) {
            append({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPeople]);
    if (subFieldDefinitions.length === 0) {
        return null;
    }
    const handleAddPerson = () => {
        if (fields.length >= maxAdditionalAttendees)
            return;
        append({});
    };
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '100%',
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: theme.palette.common.grey8,
            border: `1px solid ${theme.palette.common.grey24}`
        }, children: [_jsx(Typography, { variant: 'subtitle2', fontWeight: 500, color: 'text.primary', children: label }), fields.map((personField, personIndex) => {
                const isCollapsed = collapsedPersonIds.has(personField.id);
                return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: [personIndex > 0 && _jsx(Divider, {}), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: theme.palette.common.white,
                                border: `1px solid ${theme.palette.grey[300]}`
                            }, children: [_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        flexWrap: 'wrap'
                                    }, children: [_jsx(Typography, { variant: 'body2', fontWeight: 600, children: t('eventRegistration.form.additionalAttendeesPersonLabel', {
                                                number: personIndex + 1
                                            }) }), _jsx(Button, { variant: 'outlined', size: 'small', color: 'inherit', onClick: () => togglePersonDetails(personField.id), children: isCollapsed
                                                ? t('events.additionalAttendeesPreset.previewShowDetails')
                                                : t('events.additionalAttendeesPreset.previewHideDetails') })] }), _jsx(Collapse, { in: !isCollapsed, timeout: 'auto', children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }, children: [subFieldDefinitions.map((subField) => {
                                                const SubFieldComponent = fieldRenderers[subField.fieldType];
                                                if (!SubFieldComponent)
                                                    return null;
                                                const name = `${fieldId}.people.${personIndex}.${subField.id}`;
                                                const isEmailSubField = subField.fieldType === '2';
                                                const fieldError = get(errors, name)?.message;
                                                return (_jsx(SubFieldComponent, { name: name, control: control, fieldType: subField.fieldType, label: subField.label, disabled: disabled, error: fieldError, options: (subField.fieldOptions || []).map((option) => ({
                                                        label: option.label,
                                                        value: String(option.value),
                                                        id: option.id
                                                    })), rules: {
                                                        required: subField.isRequired
                                                            ? t('eventRegistration.form.fieldRequired')
                                                            : false,
                                                        validate: (value) => {
                                                            if (isEmailSubField &&
                                                                typeof value === 'string' &&
                                                                value.trim() !== '' &&
                                                                !EMAIL_PATTERN.test(value.trim())) {
                                                                return t('eventRegistration.form.invalidEmail');
                                                            }
                                                            return true;
                                                        }
                                                    } }, subField.id));
                                            }), !disabled && fields.length > 1 && (_jsx(Button, { variant: 'soft', color: 'error', size: 'small', fullWidth: true, onClick: () => remove(personIndex), children: t('events.additionalAttendeesPreset.previewRemovePerson') }))] }) })] })] }, personField.id));
            }), fields.length < maxAdditionalAttendees ? (!disabled && (_jsxs(_Fragment, { children: [fields.length > 0 && _jsx(Divider, {}), _jsx(Button, { variant: 'outlined', size: 'small', color: 'inherit', startIcon: _jsx(AddCircle, { size: 20, color: theme.palette.primary.darker }), onClick: handleAddPerson, sx: { alignSelf: 'flex-start' }, children: t('events.additionalAttendeesPreset.previewAddPerson') })] }))) : (_jsx(Alert, { severity: 'info', children: maxAdditionalAttendeesReachedMessage?.trim() ||
                    limitReachedMessage?.trim() ||
                    t('eventRegistration.form.additionalAttendeesMaxReached', {
                        max: maxAdditionalAttendees
                    }) }))] }));
};
export default AdditionalAttendeesGroup;
